import { MDXProvider } from '@mdx-js/react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { format } from 'date-fns'
import { Briefcase, Clock, MapPin } from 'lucide-react'
import { lazy, Suspense } from 'react'
import { MdxErrorBoundary } from '~/components/MdxErrorBoundary'
import { GradientBackground } from '~/components/backgrounds/GradientBackground'
import { MainLayout } from '~/components/layout/MainLayout'
import { Badge } from '~/components/ui/badge'
import { getJobBySlug, getJobComponent, type JobFrontmatter } from '~/lib/jobs'
import { mdxComponents } from '~/mdx-components'

export const Route = createFileRoute('/careers/$slug')({
  loader: async ({ params }) => {
    const entry = await getJobBySlug({ data: params.slug })
    const job = entry
      ? {
          slug: entry.slug,
          filePath: entry.filePath,
          frontmatter: entry.frontmatter,
          excerpt: entry.excerpt,
        }
      : null
    return { job }
  },
  component: JobPage,
})

type LoaderJob = {
  slug: string
  filePath: string
  frontmatter: JobFrontmatter
  excerpt?: string
} | null

function JobPage() {
  const { job } = Route.useLoaderData() as { job: LoaderJob }
  const { slug } = Route.useParams()

  if (!job) {
    return (
      <div className="flex min-h-screen items-center justify-center text-center">
        <div>
          <h1 className="font-sans mb-4 text-4xl">Role Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The role you&apos;re looking for doesn&apos;t exist or may no longer be open.
          </p>
          <Link to="/careers" className="text-primary inline-flex items-center hover:underline">
            ← Back to Careers
          </Link>
        </div>
      </div>
    )
  }

  const postedDisplay = formatPosted(job.frontmatter.posted)
  const importer = getJobComponent(slug)
  if (!importer && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn(`[MDX] No importer found for job "${slug}".`)
  }
  const Module = importer ? lazy(importer) : null

  return (
    <MainLayout>
      <div className="relative min-h-screen w-full">
        <div className="absolute inset-0 h-full w-full">
          <GradientBackground
            type="linear"
            position="to-br"
            stops={[
              { color: '#FFFFFF', position: 0 },
              { color: '#E5E7EB', position: 100 },
            ]}
            softness={150}
          />
        </div>

        <div className="relative pt-12 pb-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-8 lg:px-12">
            <div className="mb-8">
              <Link to="/careers" className="text-primary hover:underline">
                ← Back to all roles
              </Link>
            </div>

            <div className="space-y-8">
              <section className="bg-background/60 border-foreground/20 rounded-lg border p-8 backdrop-blur-md">
                <Badge variant="secondary" className="mb-3 w-fit">
                  {job.frontmatter.department}
                </Badge>
                <h1 className="font-sans text-3xl leading-tight tracking-tight text-gray-900 md:text-4xl">
                  {job.frontmatter.title}
                </h1>

                {job.excerpt && (
                  <p className="text-muted-foreground mt-4 text-base leading-relaxed">
                    {job.excerpt}
                  </p>
                )}

                <dl className="mt-6 grid gap-4 text-sm text-gray-700 md:grid-cols-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <div>
                      <dt className="font-semibold">Location</dt>
                      <dd>{job.frontmatter.location}</dd>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-gray-500" />
                    <div>
                      <dt className="font-semibold">Type</dt>
                      <dd>{job.frontmatter.type}</dd>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <div>
                      <dt className="font-semibold">Experience</dt>
                      <dd>{job.frontmatter.experience}</dd>
                    </div>
                  </div>
                  {postedDisplay && (
                    <div>
                      <dt className="font-semibold text-gray-700">Posted</dt>
                      <dd className="text-gray-600">{postedDisplay}</dd>
                    </div>
                  )}
                </dl>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <a
                    href={`mailto:founders@coplane.com?subject=Application for ${encodeURIComponent(job.frontmatter.title)}`}
                    className="rounded-full border border-gray-900 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:bg-gray-900 hover:text-white"
                  >
                    Apply for this role
                  </a>
                  <span className="text-xs uppercase tracking-wide text-gray-600">
                    We review every application personally.
                  </span>
                </div>
              </section>

              <section className="bg-background/60 border-foreground/20 rounded-lg border p-8 backdrop-blur-md">
                {Module && (
                  <article className="prose prose-base max-w-none dark:prose-invert [&_li]:text-foreground/90 [&_p]:text-foreground/90">
                    <MDXProvider components={mdxComponents}>
                      <MdxErrorBoundary slug={slug} sourcePath={job.filePath}>
                        <Suspense fallback={null}>
                          <Module />
                        </Suspense>
                      </MdxErrorBoundary>
                    </MDXProvider>
                  </article>
                )}
              </section>

              <section className="bg-background/60 border-foreground/20 rounded-lg border p-8 text-center backdrop-blur-md">
                <h2 className="font-sans text-2xl">Ready to apply?</h2>
                <p className="text-muted-foreground mx-auto mt-3 max-w-2xl text-sm leading-relaxed">
                  Send your resume and a short note about why you&apos;re excited about CoPlane. Our founders
                  review every application within a few days.
                </p>
                <a
                  href={`mailto:founders@coplane.com?subject=Application for ${encodeURIComponent(job.frontmatter.title)}`}
                  className="mt-6 inline-flex rounded-full border border-gray-900 px-5 py-2 text-sm font-semibold text-gray-900 transition hover:bg-gray-900 hover:text-white"
                >
                  Email founders@coplane.com
                </a>
              </section>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

function formatPosted(value: string) {
  try {
    return format(new Date(value), 'MMMM d, yyyy')
  } catch {
    return null
  }
}
